import { createSelector } from 'reselect';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import sortBy from 'lodash/sortBy';
import values from 'lodash/values';
import { NO_PRACTICE, UNASSIGNED_REQUEST_TYPE } from '../../../common/constants';
import type { Group, TicketType } from '../../../common/types';
import { GROUPS_DATA_PATH, TICKET_TYPES_DATA_PATH } from '../constants';
import type { ImmutableState, GroupsResourceStatus, TicketTypesResource } from '../types';

type TicketTypesMap = TicketTypesResource['data'];
type Relationship = number[] | null;

export const getGroupsResourceStatus = (state: ImmutableState): GroupsResourceStatus => {
	const { data, ...rest } = state.groups;
	return rest;
};
export const getTicketTypesResource = (state: ImmutableState): TicketTypesResource =>
	state.ticketTypes;
export const getGroupsSelector = (state: ImmutableState): Group[] =>
	get(state, GROUPS_DATA_PATH, []);
export const getTicketTypesSelector = (state: ImmutableState): TicketTypesMap =>
	get(state, TICKET_TYPES_DATA_PATH, {});

export const isLastGroupSelector = (state: ImmutableState): boolean =>
	getGroupsSelector(state).length === 1;

export const isNewestGroupSelector = (state: ImmutableState, groupId: number): boolean =>
	state.lastCreatedGroup === groupId;

export const getGroupSelector = (state: ImmutableState, id: number): Group | undefined =>
	find(getGroupsSelector(state), { id });

export const getTicketTypeIdsByGroupId = (state: ImmutableState, groupId: number): Relationship =>
	get(state, `ticketTypesByGroup[${groupId}]`, null);

export const getGroupIdsByTicketTypeId = (
	state: ImmutableState,
	ticketTypeId: number,
): Relationship => get(state, `groupsByTicketType[${ticketTypeId}]`, null);

export const getTicketTypesByGroupId = createSelector(
	getTicketTypesSelector,
	getTicketTypeIdsByGroupId,
	(ticketTypesMap, ticketTypeIds) =>
		ticketTypeIds ? ticketTypeIds.map((id) => ticketTypesMap[id.toString()]) : null,
);

const byTicketTypeName = ({ name }: TicketType) => name.toLowerCase();

export const getTicketTypesByPractices = createSelector(
	getTicketTypesSelector,
	({ ticketTypesByPractice }) => ticketTypesByPractice,
	(ticketTypesMap, ticketTypeIdsByPractice) => {
		if (isEmpty(ticketTypeIdsByPractice)) {
			return {
				[NO_PRACTICE]: sortBy(values(ticketTypesMap), byTicketTypeName),
			};
		}
		const ticketTypesByPractice: Record<string, TicketType[]> = reduce(
			ticketTypeIdsByPractice,
			(acc, ticketTypeIds, key) => ({
				...acc,
				[key]: sortBy(
					ticketTypeIds.map((id) => ticketTypesMap[id]),
					byTicketTypeName,
				),
			}),
			{},
		);

		const assignedTicketTypeIds: Set<number> = new Set(
			reduce(
				ticketTypeIdsByPractice,
				(acc: number[], ticketTypeIds) => [...acc, ...ticketTypeIds],
				[],
			),
		);
		const unassignedTicketTypes = values(ticketTypesMap).filter(
			(ticketType) => !assignedTicketTypeIds.has(ticketType.id),
		);

		ticketTypesByPractice[UNASSIGNED_REQUEST_TYPE] = sortBy(
			unassignedTicketTypes,
			byTicketTypeName,
		);

		return ticketTypesByPractice;
	},
);
